import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { MobileNav } from '../components/MobileNav';
import { Coins, Building2, QrCode, Upload, ArrowLeft, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config';
import { COIN_TO_IDR, formatIDRForCoins } from '../utils/coinEconomy';

const playfairFont = "'Playfair Display', serif";

export const TopUp = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [uniqueCode, setUniqueCode] = useState(null);   // 100..999, generated per top-up attempt
  const [selectedMethod, setSelectedMethod] = useState('qris');
  const [paymentProofUrl, setPaymentProofUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coinBalance, setCoinBalance] = useState(0);
  const [paymentSettings, setPaymentSettings] = useState({});
  const [showInstructions, setShowInstructions] = useState(false);
  const [packages, setPackages] = useState([
    { amount: 10000 },
    { amount: 20000 },
    { amount: 50000 },
    { amount: 100000 },
    { amount: 200000 },
    { amount: 500000 },
    { amount: 1000000 },
  ]);

  // Generate a fresh unique code (100..999) whenever the user picks a new package.
  // Code is REGENERATED on each fresh selection so concurrent users get distinct totals.
  useEffect(() => {
    if (selectedAmount || (customAmount && parseFloat(customAmount) >= 10000)) {
      setUniqueCode(Math.floor(Math.random() * 900) + 100);
    } else {
      setUniqueCode(null);
    }
  }, [selectedAmount, customAmount]);

  useEffect(() => {
    fetchCoinBalance();
    fetchPaymentSettings();
    fetchPackages();
  }, []);

  const fetchCoinBalance = async () => {
    try {
      const { data } = await axios.get(`${API}/wallet/balance`, { withCredentials: true });
      setCoinBalance(data.coin_balance);
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/payment-methods`);
      setPaymentSettings(data);
    } catch (error) {
      console.error('Failed to fetch payment settings:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      const { data } = await axios.get(`${API}/coin-packages`);
      if (data.packages?.length) setPackages(data.packages);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/uploads/payment-proof`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPaymentProofUrl(data.url);
      toast.success('Proof uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatCoins = (coins) => {
    return new Intl.NumberFormat('id-ID').format(coins);
  };

  const handleTopUp = async () => {
    const baseAmount = selectedAmount || parseInt(customAmount, 10);

    if (!baseAmount || baseAmount < 10000) {
      toast.error('Minimum top up Rp 10.000');
      return;
    }
    if (baseAmount % 1000 !== 0) {
      toast.error('Nominal harus kelipatan Rp 1.000');
      return;
    }
    if (!uniqueCode) {
      toast.error('Kode unik belum digenerasi — pilih ulang paket');
      return;
    }
    if (!paymentProofUrl || !(paymentProofUrl.startsWith('http') || paymentProofUrl.startsWith('/api/uploads/'))) {
      toast.error('Mohon upload atau paste URL bukti pembayaran');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/wallet/topup`, {
        base_amount: baseAmount,
        unique_code: uniqueCode,
        total_transfer: baseAmount + uniqueCode,
        payment_method: selectedMethod,
        payment_proof_url: paymentProofUrl,
      }, { withCredentials: true });

      toast.success(`Permintaan top-up dikirim. Coin (${Math.floor(baseAmount / COIN_TO_IDR)}) akan ditambahkan setelah admin verifikasi.`);
      navigate('/profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Top up gagal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-24" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }}>
      <header className="sticky top-0 z-50 backdrop-blur-2xl" style={{ background: 'rgba(8, 12, 10, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="hover:opacity-80">
            <ArrowLeft className="w-6 h-6" style={{ color: '#F8FAF9' }} />
          </button>
          <div>
            <h1 className="text-2xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Top Up Coins</h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Balance */}
        <div className="rounded-2xl p-8 backdrop-blur-xl relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(3, 32, 20, 0.4) 100%)', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
          <div className="flex items-center gap-3 mb-2">
            <Coins className="w-6 h-6" style={{ color: '#D4AF37' }} />
            <span className="text-sm" style={{ color: '#A3B8AC' }}>Saldo Coin</span>
          </div>
          <p className="text-4xl font-light" style={{ fontFamily: playfairFont, color: '#D4AF37' }} data-testid="topup-balance">
            {formatCoins(coinBalance)} Coin
          </p>
          <p className="text-sm mt-1" style={{ color: '#CFE0D5' }} data-testid="topup-balance-idr">
            {formatIDRForCoins(coinBalance)} · 1 Coin = Rp1.000
          </p>
        </div>

        {/* Packages */}
        <div>
          <h2 className="text-xl font-light mb-2" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
            Pilih Paket
          </h2>
          <p className="text-sm mb-6" style={{ color: '#A3B8AC' }}>1 Coin = Rp1.000. Pilih sesuai kebutuhan.</p>
          <div className="grid md:grid-cols-3 gap-4">
            {packages.map((pkg) => {
              const coins = Math.floor(pkg.amount / COIN_TO_IDR);
              return (
                <motion.button
                  key={pkg.amount}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {setSelectedAmount(pkg.amount); setCustomAmount('');}}
                  className="rounded-2xl p-6 backdrop-blur-xl transition-all"
                  style={{
                    background: selectedAmount === pkg.amount ? 'rgba(212, 175, 55, 0.2)' : 'rgba(255, 255, 255, 0.03)',
                    border: `1px solid ${selectedAmount === pkg.amount ? 'rgba(212, 175, 55, 0.5)' : 'rgba(255, 255, 255, 0.1)'}`,
                  }}
                  data-testid={`pkg-${pkg.amount}`}
                >
                  <p className="text-2xl font-light mb-1" style={{ fontFamily: playfairFont, color: '#D4AF37' }}>
                    {formatCoins(coins)} Coin
                  </p>
                  <p className="text-sm" style={{ color: '#F8FAF9' }}>
                    {formatPrice(pkg.amount)}
                  </p>
                </motion.button>
              );
            })}
          </div>

          <div className="mt-6">
            <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>Nominal custom (kelipatan Rp 1.000, min Rp 10.000)</label>
            <input
              type="number"
              value={customAmount}
              onChange={(e) => {setCustomAmount(e.target.value); setSelectedAmount(null);}}
              className="w-full rounded-lg px-4 py-3 transition-all outline-none"
              style={{ background: 'rgba(0, 0, 0, 0.2)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#F8FAF9' }}
              placeholder="cth: 250000"
              data-testid="custom-amount-input"
            />
            {customAmount && parseFloat(customAmount) >= 10000 && (
              <p className="text-xs mt-1.5" style={{ color: '#A3B8AC' }}>
                ≈ {formatCoins(Math.floor(parseFloat(customAmount) / COIN_TO_IDR))} Coin (hanya base amount)
              </p>
            )}
          </div>
        </div>

        {/* ===== Transfer Summary with Unique Code ===== */}
        {(selectedAmount || (customAmount && parseFloat(customAmount) >= 10000)) && uniqueCode && (() => {
          const base = selectedAmount || parseInt(customAmount, 10);
          const total = base + uniqueCode;
          const coins = Math.floor(base / COIN_TO_IDR);
          return (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl p-6 backdrop-blur-xl space-y-2"
              style={{ background: 'rgba(212, 175, 55, 0.08)', border: '1px solid rgba(212, 175, 55, 0.3)' }}
              data-testid="transfer-summary"
            >
              <h3 className="text-sm uppercase tracking-wider mb-3" style={{ color: '#D4AF37' }}>Total Transfer</h3>
              <div className="flex justify-between text-sm">
                <span style={{ color: '#A3B8AC' }}>Base Amount (paket coin):</span>
                <span data-testid="summary-base" style={{ color: '#F8FAF9' }}>{formatPrice(base)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: '#A3B8AC' }}>Kode Unik (untuk identifikasi):</span>
                <span data-testid="summary-unique-code" style={{ color: '#F59E0B' }}>+ Rp {uniqueCode}</span>
              </div>
              <div className="flex justify-between pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <span className="font-semibold" style={{ color: '#F8FAF9' }}>Yang harus ditransfer:</span>
                <span className="font-semibold text-lg" data-testid="summary-total" style={{ color: '#D4AF37' }}>{formatPrice(total)}</span>
              </div>
              <div className="flex justify-between text-sm pt-1">
                <span style={{ color: '#A3B8AC' }}>Coin yang didapat setelah verifikasi:</span>
                <span data-testid="summary-coins" style={{ color: '#22C55E' }}>{formatCoins(coins)} Coin</span>
              </div>
              <p className="text-xs mt-2 leading-relaxed" style={{ color: '#A3B8AC' }}>
                ⚠ Wajib transfer <strong style={{ color: '#D4AF37' }}>persis Rp {total.toLocaleString('id-ID')}</strong> agar admin bisa mencocokkan. Kode unik <strong>tidak</strong> dikonversi jadi coin.
              </p>
            </motion.div>
          );
        })()}

        {/* Payment Method */}
        <div>
          <h2 className="text-xl font-light mb-6" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
            Payment Method
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => {setSelectedMethod('qris'); setShowInstructions(true);}}
              className="rounded-xl p-4 backdrop-blur-xl transition-all flex items-center gap-3"
              style={{
                background: selectedMethod === 'qris' ? 'rgba(212, 175, 55, 0.2)' : 'rgba(255, 255, 255, 0.03)',
                border: `1px solid ${selectedMethod === 'qris' ? 'rgba(212, 175, 55, 0.5)' : 'rgba(255, 255, 255, 0.1)'}`,
              }}
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(212, 175, 55, 0.2)' }}>
                <QrCode className="w-5 h-5" style={{ color: '#D4AF37' }} />
              </div>
              <span style={{ color: '#F8FAF9' }}>QRIS</span>
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => {setSelectedMethod('mandiri'); setShowInstructions(true);}}
              className="rounded-xl p-4 backdrop-blur-xl transition-all flex items-center gap-3"
              style={{
                background: selectedMethod === 'mandiri' ? 'rgba(212, 175, 55, 0.2)' : 'rgba(255, 255, 255, 0.03)',
                border: `1px solid ${selectedMethod === 'mandiri' ? 'rgba(212, 175, 55, 0.5)' : 'rgba(255, 255, 255, 0.1)'}`,
              }}
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(212, 175, 55, 0.2)' }}>
                <Building2 className="w-5 h-5" style={{ color: '#D4AF37' }} />
              </div>
              <span style={{ color: '#F8FAF9' }}>Bank Mandiri</span>
            </motion.button>
          </div>

          {/* Payment Instructions */}
          {showInstructions && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 rounded-2xl p-6 backdrop-blur-xl"
              style={{ background: 'rgba(3, 32, 20, 0.4)', border: '1px solid rgba(212, 175, 55, 0.3)' }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5" style={{ color: '#D4AF37' }} />
                <h3 className="font-medium" style={{ color: '#F8FAF9' }}>Payment Instructions</h3>
              </div>

              {selectedMethod === 'qris' && paymentSettings.qris_image_url ? (
                <div>
                  <p className="text-sm mb-4" style={{ color: '#A3B8AC' }}>Scan the QR code below to pay:</p>
                  <img src={paymentSettings.qris_image_url} alt="QRIS" className="max-w-xs mx-auto rounded-lg" />
                </div>
              ) : selectedMethod === 'mandiri' ? (
                <div className="space-y-2">
                  <p className="text-sm" style={{ color: '#A3B8AC' }}>Transfer to:</p>
                  <p className="font-mono font-bold text-lg" style={{ color: '#D4AF37' }}>
                    {paymentSettings.mandiri_account_number || 'Not configured'}
                  </p>
                  <p className="text-sm" style={{ color: '#A3B8AC' }}>
                    a.n. {paymentSettings.mandiri_account_holder || 'Not configured'}
                  </p>
                </div>
              ) : (
                <p className="text-sm" style={{ color: '#A3B8AC' }}>Payment method not configured yet</p>
              )}
            </motion.div>
          )}
        </div>

        {/* Upload Proof */}
        <div>
          <h2 className="text-xl font-light mb-6" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
            Upload Payment Proof
          </h2>
          <div className="rounded-2xl p-6 backdrop-blur-xl space-y-4" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <label className="flex items-center justify-center gap-3 w-full py-4 rounded-xl cursor-pointer transition-all" style={{ background: 'rgba(212,175,55,0.15)', border: '1px dashed rgba(212,175,55,0.5)' }} data-testid="upload-proof-label">
              <Upload className="w-5 h-5" style={{ color: '#D4AF37' }} />
              <span style={{ color: '#D4AF37' }}>{uploading ? 'Uploading...' : 'Choose Image (max 5MB)'}</span>
              <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp,image/gif" onChange={handleFileUpload} className="hidden" disabled={uploading} data-testid="upload-proof-input" />
            </label>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
              <span className="text-xs" style={{ color: '#A3B8AC' }}>OR paste image URL</span>
              <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
            </div>

            <input
              type="url"
              value={paymentProofUrl}
              onChange={(e) => setPaymentProofUrl(e.target.value)}
              className="w-full rounded-lg px-4 py-3 transition-all outline-none"
              style={{ background: 'rgba(0, 0, 0, 0.2)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#F8FAF9' }}
              placeholder="https://example.com/your-payment-proof.jpg"
              data-testid="proof-url-input"
            />

            {paymentProofUrl && (
              <div>
                <p className="text-sm mb-2" style={{ color: '#A3B8AC' }}>Preview:</p>
                <img
                  src={paymentProofUrl}
                  alt="Payment Proof"
                  className="max-w-sm rounded-lg"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
          </div>
        </div>

        {/* Submit */}
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={handleTopUp}
          disabled={loading || (!selectedAmount && !customAmount) || !paymentProofUrl}
          className="w-full rounded-full py-4 font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ background: '#D4AF37', color: '#080C0A' }}
        >
          {loading ? 'Submitting...' : (
            <>
              <CheckCircle className="w-5 h-5" />
              Submit for Verification
            </>
          )}
        </motion.button>

        <div className="text-center p-4 rounded-xl" style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.2)' }}>
          <p className="text-sm" style={{ color: '#A3B8AC' }}>
            Your request will be reviewed by admin. Coins will be added after verification.
          </p>
        </div>
      </div>

      <MobileNav />
    </div>
  );
};
